import { useCallback, useRef, useState } from "react";

import type { RequestableVoiceEntry } from "~/features/meditations/hooks/use-meditation-custom-voice";

// AIDEV-NOTE: One-time client-facing promo for practitioner voice, shown when a client
// presses play/generate and none of their practitioners has a voice yet. It exists to
// keep some promotional pull for the feature BEFORE any practitioner has set one up.
//
// It is a gate, not an interruption: whatever the client chooses, the original play
// proceeds afterwards. Shown once ever — a therapy app should not nag, so dismissing
// without asking also retires it.
//
// Persisted in localStorage rather than server-side on purpose: this is a one-time
// marketing beat, not entitlement state, and a per-device re-show is a far cheaper
// failure than adding a table and a round trip to the play path.
const SEEN_KEY = "practitioner-voice-promo-seen";

function hasSeenPromo(): boolean {
    if (typeof window === "undefined") return true;
    try {
        return window.localStorage.getItem(SEEN_KEY) === "1";
    } catch {
        // Storage unavailable (private mode) — treat as seen so we never trap a user
        // in a modal we can't remember dismissing.
        return true;
    }
}

// AIDEV-NOTE: MODULE-level, not per-hook. part-meditation-button mounts one hook
// instance PER PART, so a per-instance ref only guards double-presses of the same
// button: a different instance would see its own `false`, find the seen-flag already
// set by the first, and sail through. Only one promo may be open app-wide.
let promoOpenOwner: symbol | null = null;

function markPromoSeen() {
    try {
        window.localStorage.setItem(SEEN_KEY, "1");
    } catch {
        // Ignore — worst case it shows once more on this device.
    }
}

export interface PractitionerVoicePromo {
    /** The practitioner being offered; null when the promo shouldn't show. */
    target: RequestableVoiceEntry | null;
    isOpen: boolean;
    /**
     * Wrap a play/generate handler. Returns true when the caller should proceed
     * immediately; false means the promo opened and will run the action afterwards.
     */
    interceptPlay: (proceed: () => void) => boolean;
    /** Close the promo and run the pending play. */
    resolve: () => void;
}

export function usePractitionerVoicePromo({
    requestableVoices,
    hasReadyVoice,
    enabled = true,
}: {
    requestableVoices: RequestableVoiceEntry[];
    /** When any practitioner already has a voice there is nothing to promote. */
    hasReadyVoice: boolean;
    enabled?: boolean;
}): PractitionerVoicePromo {
    const [isOpen, setIsOpen] = useState(false);
    const pendingPlayRef = useRef<(() => void) | null>(null);
    // Identity for this hook instance, so it can tell "my promo" from "someone else's".
    const instanceIdRef = useRef<symbol>(Symbol("voice-promo"));
    // AIDEV-NOTE: Read synchronously by interceptPlay. Using state here would let a
    // second press in the same tick see a stale `false`.
    const isOpenRef = useRef(false);

    // Offer the longest-standing practitioner who hasn't already been asked.
    const target =
        requestableVoices.find((entry) => !entry.requested) ??
        requestableVoices[0] ??
        null;

    const interceptPlay = useCallback(
        (proceed: () => void) => {
            // Repeat press of the SAME control while its promo is open — swallow it, the
            // pending play is already queued and will run on resolve.
            if (isOpenRef.current) return false;

            // AIDEV-NOTE: A DIFFERENT control pressed while someone else's promo is open
            // passes straight through instead of being swallowed. Swallowing would lose
            // that play entirely — this instance has no dialog to resolve it — and the
            // user deliberately pressed a second, different play button. What must not
            // happen is two promos stacking, which this prevents.
            if (promoOpenOwner !== null) return true;

            if (
                !enabled ||
                hasReadyVoice ||
                !target ||
                target.requested ||
                hasSeenPromo()
            ) {
                return true;
            }
            markPromoSeen();
            pendingPlayRef.current = proceed;
            isOpenRef.current = true;
            promoOpenOwner = instanceIdRef.current;
            setIsOpen(true);
            return false;
        },
        [enabled, hasReadyVoice, target],
    );

    const resolve = useCallback(() => {
        isOpenRef.current = false;
        if (promoOpenOwner === instanceIdRef.current) promoOpenOwner = null;
        setIsOpen(false);
        const pending = pendingPlayRef.current;
        pendingPlayRef.current = null;
        // AIDEV-NOTE: The play the client actually asked for always runs, whether they
        // sent a request or dismissed. The promo must never cost them the thing they
        // pressed play for.
        pending?.();
    }, []);

    return { target, isOpen, interceptPlay, resolve };
}
